/* eslint-disable global-require */
/* eslint-disable no-plusplus */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Clear from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import useTheme from '@mui/styles/useTheme';
import GetAppIcon from '@mui/icons-material/GetApp';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuItem from '@mui/material/MenuItem';
import React, { useCallback, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from '@mui/styles/styled';
import CircularProgress from '@mui/material/CircularProgress';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import Error from '../../components/Error';
import Success from '../../components/Success';
import { useDocumentsApi } from '../../apis/hooks/useDocumentsApi';
import { downloadFileFromApi } from '../../apis/Utilities/downloadFileFromApi';
import { downloadFilesAsZipFromApi } from '../../apis/Utilities/downloadFilesAsZipFromApi';

const SMenuItem = styled(MenuItem)(({ theme }) => ({
	cursor: 'pointer',
	padding: theme.spacing(2),
}));

const mockFetch = () =>
	new Promise((resolve) => {
		setTimeout(() => resolve(), 3000);
	});

function Download({ children, isLoading, documents = [], ...props }) {
	const theme = useTheme();
	const [loadingId, setLoadingId] = useState({});

	const clickHandler = async (e) => {
		const { id } = e.currentTarget;
		setLoadingId((ids) => ({
			...ids,
			[id]: true,
		}));
		try {
			await mockFetch();
		} catch {
			console.log('error');
		} finally {
			setLoadingId((ids) => ({
				...ids,
				[id]: false,
			}));
		}
	};

	return (
		<SMenuItem {...props}>
			<Grid container spacing={2} sx={{ height: theme.spacing(5) }}>
				<Grid item xs={11}>
					<Typography
						variant="body"
						sx={{
							marginTop: theme.spacing(0.1),
							paddingleft: theme.spacing(5),
						}}
					>
						{children}
					</Typography>
				</Grid>
				<Grid
					item
					xs={1}
					sx={{
						textAlign: 'right',
					}}
				>
					<Box
						sx={{ position: 'relative' }}
						id={documents}
						onClick={clickHandler}
					>
						{loadingId[documents] ? (
							<CircularProgress
								size={20}
								color="primary"
								sx={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									marginTop: { xs: '.075rem', sm: '0.1rem' },
									marginLeft: { xs: '-.43rem', sm: '-0.2rem' },
								}}
							/>
						) : (
							<ListItemIcon
								sx={{
									marginTop: {
										xs: theme.spacing(0.25),
										sm: theme.spacing(0.5),
									},
									marginBottom: theme.spacing(-0.15),
									marginRight: {
										xs: theme.spacing(0.2),
										sm: theme.spacing(-1),
									},
									marginLeft: {
										xs: theme.spacing(-0.2),
										sm: theme.spacing(1),
									},
									opacity: loadingId[documents] ? 0 : 1,
								}}
								role="graphics-symbol"
								aria-hidden="true"
							>
								<GetAppIcon
									fontSize="small"
									color="primary"
									role="graphics-symbol"
									aria-hidden="true"
								/>
							</ListItemIcon>
						)}
					</Box>
				</Grid>
			</Grid>
		</SMenuItem>
	);
}

Download.propTypes = {
	children: PropTypes.any.isRequired,
	isLoading: PropTypes.bool,
	onClick: PropTypes.func,
	documents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function DocumentsDownloader({ documents }) {
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.down('sm'));
	const { t } = useTranslation();
	const isDisabled = !documents?.length;
	const anchorRef = React.useRef(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [fileType, setFileType] = useState(null);
	const isMountedRef = useIsMountedRef();
	const [success, setSuccess] = useState(null);
	const [error, setError] = useState(null);

	const openMenu = (e) =>
		e.stopPropagation() || (!isDisabled && setAnchorEl(e.currentTarget));
	const closeMenu = () => setAnchorEl(null);

	const docInvoice = documents.filter((item) => item.type === 'Invoice');
	const docCOA = documents.filter((item) => item.type === 'COA');
	const docBOL = documents.filter((item) => item.type === 'BOL');
	const { getFile } = useDocumentsApi();

	const setDownloadSuccessState = useCallback(() => {
		setIsLoading(false);
		window.localStorage.removeItem('error');
		window.localStorage.setItem('success', 'true');
		window.dispatchEvent(new Event('storage'));
		setError(false);
		setSuccess(true);

		setTimeout(() => {
			window.localStorage.removeItem('success');
			window.dispatchEvent(new Event('storage'));
			setSuccess(false);
		}, 3000);
	}, []);

	const setDownloadErrorState = useCallback((err) => {
		console.log(err);
		setIsLoading(false);
		window.localStorage.setItem('error', 'true');
		window.dispatchEvent(new Event('storage'));
	}, []);

	// -------------------------------------------------------------
	// Given a single file, download it as a pdf file.
	// -------------------------------------------------------------
	const getSinglePdf = useCallback(
		async ({ fileName, id }) => {
			if (!isMountedRef.current) {
				return;
			}

			const isDownloaded = await downloadFileFromApi({
				format: 'pdf',
				defaultFilenamePrefix: 'Unknown',
				executeApiCall: () => getFile({ fileName, id }),
				onError: setDownloadErrorState,
			});

			if (isMountedRef.current && isDownloaded) {
				setDownloadSuccessState();
			}
		},
		[getFile, isMountedRef, setDownloadErrorState, setDownloadSuccessState]
	);

	// -------------------------------------------------------------
	// Given a list of files, download them all as a zip file.
	// The zip file will be named based on the labelKey provided.
	// -------------------------------------------------------------
	const downloadGroupedDocumentsAsZip = useCallback(
		async (files, labelKey) => {
			if (!isMountedRef.current) {
				return;
			}

			const date = new Date();
			const isDownloaded = await downloadFilesAsZipFromApi({
				files,
				zipFilename: `${t(labelKey)} - ${date.toISOString()}.zip`,
				executeApiCall: ({ fileName, id }) => getFile({ fileName, id }),
				onError: setDownloadErrorState,
			});

			if (isMountedRef.current && isDownloaded) {
				setDownloadSuccessState();
			}
		},
		[getFile, isMountedRef, setDownloadErrorState, setDownloadSuccessState, t]
	);

	const desktopMenu = (
		<Menu
			id="documents-menu"
			anchorEl={anchorEl}
			keepMounted
			open={Boolean(anchorEl)}
			onClose={closeMenu}
			onClick={(e) => e.stopPropagation()}
			defaultValue="xs"
			sx={{
				'& .MuiPaper-root': {
					width: '500px',
					maxWidth: '100%',
				},
			}}
			data-testid="documents-menu-trigger"
		>
			<Grid
				container
				sx={{
					borderBottom: 1,
					borderColor: '#D3DAE6',
					paddingTop: theme.spacing(1),
					height: theme.spacing(5),
				}}
			>
				<Grid item xs={12}>
					<Grid container>
						<Grid
							item
							xs={10}
							sx={{
								marginTop: theme.spacing(-0.5),
								paddingLeft: theme.spacing(2.05),
							}}
						>
							<Typography variant="downloadHeader">
								{t('download:download_documents')}
							</Typography>
						</Grid>
						<Grid
							item
							xs={2}
							variant="downloadHeader"
							onClick={closeMenu}
							data-testid="close-menu-trigger"
							sx={{
								marginTop: theme.spacing(-0.5),
								textAlign: 'right',
								paddingRight: theme.spacing(1.5),
								display: { xs: 'block', sm: 'none' },
							}}
						>
							<Clear />
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid container>
				{docBOL.length ? (
					<>
						<Grid item xs={12}>
							<Grid
								container
								variant="heading7"
								sx={{
									borderBottom: 1,
									borderColor: '#D3DAE6',
									paddingTop: theme.spacing(2),
									height: theme.spacing(5),
								}}
							>
								<Grid item xs={6}>
									<Typography
										variant="downloadSubHeader"
										sx={{
											marginLeft: theme.spacing(2.15),
										}}
									>
										{t('download:BOL')}
									</Typography>
								</Grid>
								<Grid
									item
									xs={6}
									sx={{
										textAlign: 'right',
										paddingRight: theme.spacing(1),
									}}
								>
									<Button
										variant="text"
										onClick={() => {
											downloadGroupedDocumentsAsZip(docBOL, 'download:BOL');
											setFileType('BOL');
											setIsLoading(true);
										}}
										disabled={docBOL.length === 1}
										sx={{
											color: theme.palette.base.main,
											textDecoration: 'underline',
											cursor: 'pointer',
											textTransform: 'capitalize',
											backgroundColor: 'transparent',
											'&.Mui-disabled': {
												opacity: theme.palette.disabled.opacity,
												textDecoration: 'none',
												textTransform: 'capitalize',
												backgroundColor: 'transparent',
											},
										}}
										data-testid="download-all-button"
										className="downloadAllBOLs-btn"
									>
										{isLoading && fileType === 'BOL' ? (
											<CircularProgress
												size={20}
												color="primary"
												sx={{
													marginTop: '-1rem',
													marginRight: '-1.95rem',
												}}
											/>
										) : (
											<Typography variant="downloadSubHeader">
												{t('download:download_all')}
											</Typography>
										)}
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							{docBOL.map(({ fileName, id }) => (
								<Grid
									key={fileName}
									container
									spacing={2}
									sx={{
										borderBottom: 1,
										borderColor: '#D3DAE6',
									}}
								>
									<Grid item xs={12}>
										<Download
											onClick={() => {
												getSinglePdf({ fileName, id });
											}}
											className="downloadSingleBOLs-btn"
											data-testid="desktop-downloadSingleBOLs-btn"
											documents={id}
										>
											<span
												style={{
													paddingLeft: theme.spacing(2),
													whiteSpace: 'nowrap',
													textOverflow: 'ellipsis',
													display: 'block',
													overflow: 'hidden',
												}}
											>
												{fileName}
											</span>
										</Download>
									</Grid>
								</Grid>
							))}
						</Grid>
					</>
				) : (
					''
				)}
				{docCOA.length ? (
					<>
						<Grid item xs={12}>
							<Grid
								container
								variant="heading7"
								sx={{
									borderBottom: 1,
									borderColor: '#D3DAE6',
									paddingTop: theme.spacing(2),
									height: theme.spacing(5),
								}}
							>
								<Grid item xs={9}>
									<Typography
										variant="downloadSubHeader"
										sx={{
											marginLeft: theme.spacing(2.15),
										}}
									>
										{t('download:COA')}
									</Typography>
								</Grid>
								<Grid
									item
									xs={3}
									sx={{
										textAlign: 'right',
										paddingRight: theme.spacing(1.25),
									}}
								>
									<Button
										variant="text"
										onClick={() => {
											downloadGroupedDocumentsAsZip(docCOA, 'download:COA');
											setFileType('COA');
											setIsLoading(true);
										}}
										disabled={docCOA.length === 1}
										sx={{
											color: theme.palette.base.main,
											textDecoration: 'underline',
											cursor: 'pointer',
											textTransform: 'capitalize',
											backgroundColor: 'transparent',
											'&.Mui-disabled': {
												opacity: theme.palette.disabled.opacity,
												textDecoration: 'none',
												textTransform: 'capitalize',
												backgroundColor: 'transparent',
											},
										}}
										data-testid="download-all-coa-button"
										className="downloadAllCoa-btn"
									>
										{isLoading && fileType === 'COA' ? (
											<CircularProgress
												size={20}
												color="primary"
												sx={{
													marginTop: '-1rem',
													marginRight: '-1.95rem',
												}}
											/>
										) : (
											<Typography variant="downloadSubHeader">
												{t('download:download_all')}
											</Typography>
										)}
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							{docCOA.map(({ fileName, id, index }) => (
								<Grid
									key={fileName}
									container
									spacing={2}
									sx={{
										borderBottom: 1,
										borderColor: '#D3DAE6',
									}}
								>
									<Grid item xs={12}>
										<Download
											onClick={() => getSinglePdf({ fileName, id })}
											className="downloadSingleCOA-btn"
											data-testid="desktop-downloadSingleCOA-btn"
											documents={id}
										>
											<span
												style={{
													paddingLeft: theme.spacing(2),
													whiteSpace: 'nowrap',
													textOverflow: 'ellipsis',
													display: 'block',
													overflow: 'hidden',
												}}
											>
												{fileName}
											</span>
										</Download>
									</Grid>
								</Grid>
							))}
						</Grid>
					</>
				) : (
					''
				)}
				{docInvoice.length ? (
					<>
						<Grid item xs={12}>
							<Grid
								container
								variant="heading7"
								sx={{
									borderBottom: 1,
									borderColor: '#D3DAE6',
									paddingTop: theme.spacing(2),
									height: theme.spacing(5),
								}}
							>
								<Grid item xs={9}>
									<Typography
										variant="downloadSubHeader"
										sx={{
											marginLeft: theme.spacing(2.15),
										}}
									>
										{t('download:invoices')}
									</Typography>
								</Grid>
								<Grid
									item
									xs={3}
									sx={{
										textAlign: 'right',
										paddingRight: theme.spacing(1.25),
									}}
								>
									<Button
										variant="text"
										onClick={() => {
											downloadGroupedDocumentsAsZip(
												docInvoice,
												'download:invoices'
											);
											setFileType('Invoice');
											setIsLoading(true);
										}}
										disabled={docInvoice.length === 1}
										sx={{
											color: theme.palette.base.main,
											textDecoration: 'underline',
											cursor: 'pointer',
											textTransform: 'capitalize',
											backgroundColor: 'transparent',
											'&.Mui-disabled': {
												opacity: theme.palette.disabled.opacity,
												textDecoration: 'none',
												textTransform: 'capitalize',
												backgroundColor: 'transparent',
											},
										}}
										data-testid="download-all-invoice-button"
										className="downloadAllInvoices-btn"
									>
										{isLoading && fileType === 'Invoice' ? (
											<CircularProgress
												size={20}
												color="primary"
												sx={{
													marginTop: '-1rem',
													marginRight: '-1.95rem',
												}}
											/>
										) : (
											<Typography variant="downloadSubHeader">
												{t('download:download_all')}
											</Typography>
										)}
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							{docInvoice.map(({ fileName, id, index }) => (
								<Grid
									key={fileName}
									container
									spacing={2}
									sx={{
										borderBottom: 1,
										borderColor: '#D3DAE6',
									}}
								>
									<Grid item xs={12}>
										<Download
											onClick={() => getSinglePdf({ fileName, id })}
											className="downloadSingleInvoice-btn"
											data-testid="desktop-downloadSingleInvoice-btn"
											documents={id}
										>
											<span
												style={{
													paddingLeft: theme.spacing(2),
													whiteSpace: 'nowrap',
													textOverflow: 'ellipsis',
													display: 'block',
													overflow: 'hidden',
												}}
											>
												{fileName}
											</span>
										</Download>
									</Grid>
								</Grid>
							))}
						</Grid>
					</>
				) : (
					''
				)}
			</Grid>
		</Menu>
	);

	const mobileMenu = (
		<Menu
			id="documents-menu"
			anchorEl={anchorEl}
			keepMounted
			open={Boolean(anchorEl)}
			onClose={closeMenu}
			onClick={(e) => e.stopPropagation()}
			sx={{
				'& .MuiPaper-root': {
					width: '100%',
					height: '100%',
					top: '0 !important',
					left: '0 !important',
					maxWidth: '100%',
					maxHeight: '100%',
				},
			}}
			data-testid="documents-mobile-menu-trigger"
		>
			<Grid
				container
				sx={{
					borderBottom: 1,
					borderColor: '#D3DAE6',
					paddingTop: theme.spacing(1),
					height: theme.spacing(5),
				}}
			>
				<Grid item xs={12}>
					<Grid container>
						<Grid
							item
							xs={10}
							sx={{
								marginTop: theme.spacing(-0.5),
								paddingLeft: theme.spacing(2.05),
							}}
						>
							<Typography variant="downloadHeader">
								{t('download:download_documents')}
							</Typography>
						</Grid>
						<Grid
							item
							xs={2}
							variant="downloadHeader"
							onClick={closeMenu}
							data-testid="close-mobile-menu-trigger"
							sx={{
								marginTop: theme.spacing(-0.5),
								textAlign: 'right',
								paddingRight: theme.spacing(1.5),
							}}
						>
							<Clear />
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			{success && (
				<Grid container>
					<Grid
						item
						xs={12}
						sx={{
							paddingBottom: '0',
							paddingLeft: theme.spacing(1.8),
							paddingRight: theme.spacing(1.8),
							paddingTop: theme.spacing(1.8),
						}}
					>
						<Success message={t('download:success')} />
					</Grid>
				</Grid>
			)}
			{error && !success && (
				<Grid container>
					<Grid
						item
						xs={12}
						sx={{
							paddingBottom: '0',
							paddingLeft: theme.spacing(1.8),
							paddingRight: theme.spacing(1.8),
							paddingTop: theme.spacing(1.8),
						}}
					>
						<Error message={t('download:error')} />
					</Grid>
				</Grid>
			)}

			<Grid container>
				{docBOL.length ? (
					<>
						<Grid item xs={12}>
							<Grid
								container
								variant="heading7"
								sx={{
									borderBottom: 1,
									borderColor: '#D3DAE6',
									paddingTop: theme.spacing(2),
									height: theme.spacing(5),
								}}
							>
								<Grid item xs={8}>
									<Typography
										variant="downloadSubHeader"
										sx={{
											marginLeft: theme.spacing(2.15),
										}}
									>
										{t('download:BOL')}
									</Typography>
								</Grid>
								<Grid
									item
									xs={4}
									sx={{
										textAlign: 'right',
										paddingRight: theme.spacing(1),
										cursor: 'pointer',
									}}
									data-testid="download-all-mobile-button"
									onClick={() => {
										downloadGroupedDocumentsAsZip(docBOL, 'download:BOL');
										setFileType('BOL');
										setIsLoading(true);
									}}
								>
									<Button
										variant="text"
										disabled={docBOL.length === 1}
										sx={{
											color: theme.palette.base.main,
											textDecoration: 'underline',
											cursor: 'pointer',
											textTransform: 'capitalize',
											backgroundColor: 'transparent',
											'&.Mui-disabled': {
												opacity: theme.palette.disabled.opacity,
												textDecoration: 'none',
												textTransform: 'capitalize',
												backgroundColor: 'transparent',
											},
										}}
										className="downloadAllBOLs-btn"
									>
										{isLoading && fileType === 'BOL' ? (
											<CircularProgress
												size={20}
												color="primary"
												sx={{
													marginTop: '-1rem',
													marginRight: '-1.95rem',
												}}
											/>
										) : (
											<Typography variant="downloadSubHeader">
												{t('download:download_all')}
											</Typography>
										)}
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							{docBOL.map(({ fileName, id }) => (
								<Grid
									key={fileName}
									container
									spacing={2}
									sx={{
										borderBottom: 1,
										borderColor: '#D3DAE6',
									}}
								>
									<Grid item xs={12}>
										<Download
											onClick={() => getSinglePdf({ fileName, id })}
											className="downloadSingleBOLs-btn"
											documents={id}
										>
											<span
												style={{
													paddingLeft: theme.spacing(2),
													whiteSpace: 'nowrap',
													textOverflow: 'ellipsis',
													display: 'block',
													overflow: 'hidden',
												}}
											>
												{fileName}
											</span>
										</Download>
									</Grid>
								</Grid>
							))}
						</Grid>
					</>
				) : (
					''
				)}
				{docCOA.length ? (
					<>
						<Grid item xs={12}>
							<Grid
								container
								variant="heading7"
								sx={{
									borderBottom: 1,
									borderColor: '#D3DAE6',
									paddingTop: theme.spacing(2),
									height: theme.spacing(5),
								}}
							>
								<Grid item xs={8}>
									<Typography
										variant="downloadSubHeader"
										sx={{
											paddingLeft: theme.spacing(2.15),
										}}
									>
										{t('download:COA')}
									</Typography>
								</Grid>
								<Grid
									item
									xs={4}
									sx={{
										textAlign: 'right',
										paddingRight: theme.spacing(1),
										cursor: 'pointer',
									}}
									onClick={() => {
										downloadGroupedDocumentsAsZip(docCOA, 'download:COA');
										setFileType('COA');
										setIsLoading(true);
									}}
								>
									<Button
										variant="text"
										disabled={docCOA.length === 1}
										sx={{
											color: theme.palette.base.main,
											textDecoration: 'underline',
											cursor: 'pointer',
											textTransform: 'capitalize',
											backgroundColor: 'transparent',
											'&.Mui-disabled': {
												opacity: theme.palette.disabled.opacity,
												textDecoration: 'none',
												textTransform: 'capitalize',
												backgroundColor: 'transparent',
											},
										}}
										className="downloadAllCOA-btn"
									>
										{isLoading && fileType === 'COA' ? (
											<CircularProgress
												size={20}
												color="primary"
												sx={{
													marginTop: '-1rem',
													marginRight: '-1.95rem',
												}}
											/>
										) : (
											<Typography variant="downloadSubHeader">
												{t('download:download_all')}
											</Typography>
										)}
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							{docCOA.map(({ fileName, id }) => (
								<Grid
									key={fileName}
									container
									spacing={2}
									variant="body"
									sx={{
										borderBottom: 1,
										borderColor: '#D3DAE6',
									}}
								>
									<Grid item xs={12}>
										<Download
											onClick={() => getSinglePdf({ fileName, id })}
											className="downloadSingleCOA-btn"
											documents={id}
										>
											<span
												style={{
													paddingLeft: theme.spacing(2),
													whiteSpace: 'nowrap',
													textOverflow: 'ellipsis',
													display: 'block',
													overflow: 'hidden',
												}}
											>
												{fileName}
											</span>
										</Download>
									</Grid>
								</Grid>
							))}
						</Grid>
					</>
				) : (
					''
				)}
				{docInvoice.length ? (
					<>
						<Grid item xs={12}>
							<Grid
								container
								variant="heading7"
								sx={{
									borderBottom: 1,
									borderColor: '#D3DAE6',
									paddingTop: theme.spacing(2),
									height: theme.spacing(5),
								}}
							>
								<Grid item xs={8}>
									<Typography
										variant="downloadSubHeader"
										sx={{
											paddingLeft: theme.spacing(2.15),
										}}
									>
										{t('download:invoices')}
									</Typography>
								</Grid>
								<Grid
									item
									xs={4}
									sx={{
										textAlign: 'right',
										paddingRight: theme.spacing(1),
										cursor: 'pointer',
									}}
									onClick={() => {
										downloadGroupedDocumentsAsZip(
											docInvoice,
											'download:invoices'
										);
										setFileType('Invoice');
										setIsLoading(true);
									}}
								>
									<Button
										variant="text"
										disabled={docInvoice.length === 1}
										sx={{
											color: theme.palette.base.main,
											textDecoration: 'underline',
											cursor: 'pointer',
											textTransform: 'capitalize',
											backgroundColor: 'transparent',
											'&.Mui-disabled': {
												opacity: theme.palette.disabled.opacity,
												textDecoration: 'none',
												textTransform: 'capitalize',
												backgroundColor: 'transparent',
											},
										}}
										className="downloadAllInvoices-btn"
									>
										{isLoading && fileType === 'Invoice' ? (
											<CircularProgress
												size={20}
												color="primary"
												sx={{
													marginTop: '-1rem',
													marginRight: '-1.95rem',
												}}
											/>
										) : (
											<Typography variant="downloadSubHeader">
												{t('download:download_all')}
											</Typography>
										)}
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							{docInvoice.map(({ fileName, id }) => (
								<Grid
									key={fileName}
									container
									spacing={2}
									variant="body"
									sx={{
										borderBottom: 1,
										borderColor: '#D3DAE6',
									}}
								>
									<Grid item xs={12}>
										<Download
											onClick={() => getSinglePdf({ fileName, id })}
											className="downloadSingleInvoice-btn"
											documents={id}
										>
											<span
												style={{
													paddingLeft: theme.spacing(2),
													whiteSpace: 'nowrap',
													textOverflow: 'ellipsis',
													display: 'block',
													overflow: 'hidden',
												}}
											>
												{fileName}
											</span>
										</Download>
									</Grid>
								</Grid>
							))}
						</Grid>
					</>
				) : (
					''
				)}
			</Grid>
		</Menu>
	);

	return (
		<>
			<Button
				size="small"
				variant="outlined"
				disabled={documents.length >= 1 ? false : isDisabled}
				onClick={openMenu}
				onKeyPress={openMenu}
				ref={anchorRef}
				role="button"
				tabIndex={isDisabled ? undefined : 0}
				sx={{ textTransform: 'Capitalize' }}
				className="download-btn"
				data-testid="download-button"
			>
				{t('download:download')}
				<ArrowDropDownIcon
					role="graphics-symbol"
					aria-hidden="true"
					style={{
						marginLeft: theme.spacing(0.75),
						marginRight: theme.spacing(-1),
					}}
				/>
			</Button>
			{matches ? mobileMenu : desktopMenu}
		</>
	);
}

DocumentsDownloader.propTypes = {
	documents: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			fileName: PropTypes.string.isRequired,
			type: PropTypes.string,
		})
	),
};

export default React.memo(DocumentsDownloader);
